import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { trackMetaEvent } from '@/hooks/useMetaPixel';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string, turnstileToken: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, name: string, turnstileToken: string) => Promise<{ error: any }>;
  resetPassword: (email: string, turnstileToken: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const loadingRef = useRef(true);
  const hasTrackedRegistration = useRef(false);
  const previousUserId = useRef<string | null>(null);
  const navigate = useNavigate();

  // Sync ref with state
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    // Safety timeout - prevent infinite loading in edge cases
    const safetyTimeout = setTimeout(() => {
      if (loadingRef.current) {
        console.warn('[Auth] Loading timeout reached - forcing state reset');
        setLoading(false);
      }
    }, 10000);

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('[Auth] Event:', event);
        
        // Handle error/logout events explicitly
        if (event === 'SIGNED_OUT') {
          console.log('[Auth] User signed out - clearing state');
          setUser(null);
          setSession(null);
          setLoading(false);
          previousUserId.current = null;
          hasTrackedRegistration.current = false;
          return;
        }
        
        // Handle token refresh failure (session will be null)
        if (event === 'TOKEN_REFRESHED' && !session) {
          console.warn('[Auth] Token refresh failed - clearing state');
          setUser(null);
          setSession(null);
          setLoading(false);
          return;
        }
        
        // Track CompleteRegistration when user confirms email and signs in for the first time
        // This fires when a new user confirms their email (SIGNED_IN event with a new user)
        if (event === 'SIGNED_IN' && session?.user) {
          const currentUserId = session.user.id;
          const isNewUser = previousUserId.current !== currentUserId;
          
          // Check if this is a user who just confirmed their email
          // email_confirmed_at will be set after email confirmation
          const emailConfirmedAt = session.user.email_confirmed_at;
          const createdAt = session.user.created_at;
          
          if (emailConfirmedAt && createdAt && isNewUser && !hasTrackedRegistration.current) {
            // Check if email was confirmed within the last 5 minutes of account creation
            // This indicates a fresh registration confirmation
            const emailConfirmedDate = new Date(emailConfirmedAt);
            const createdDate = new Date(createdAt);
            const timeDiff = emailConfirmedDate.getTime() - createdDate.getTime();
            const fiveMinutes = 5 * 60 * 1000;
            
            if (timeDiff <= fiveMinutes) {
              console.log('[Auth] New user confirmed email - tracking CompleteRegistration');
              trackMetaEvent('CompleteRegistration', {
                content_name: 'Email Signup',
                status: true
              });
              hasTrackedRegistration.current = true;
            }
          }
          
          previousUserId.current = currentUserId;
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // THEN check for existing session with error handling
    supabase.auth.getSession()
      .then(({ data: { session }, error }) => {
        if (error) {
          console.error('[Auth] getSession error:', error.message);
          setUser(null);
          setSession(null);
        } else {
          setSession(session);
          setUser(session?.user ?? null);
          if (session?.user) {
            previousUserId.current = session.user.id;
          }
        }
      })
      .catch((err) => {
        console.error('[Auth] getSession exception:', err);
        setUser(null);
        setSession(null);
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      clearTimeout(safetyTimeout);
      subscription.unsubscribe();
    };
  }, []);

  const signInWithGoogle = async () => {
    const redirectUrl = `${window.location.origin}/dashboard`;
    
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl,
      }
    });

    if (error) {
      throw error;
    }
  };

  const signInWithEmail = async (email: string, password: string, turnstileToken: string) => {
    // First, verify the Turnstile token
    try {
      const { data: verifyData, error: verifyError } = await supabase.functions.invoke('verify-turnstile', {
        body: { token: turnstileToken }
      });

      if (verifyError) {
        console.error('Turnstile verification error:', verifyError);
        return { error: { message: 'Verification failed. Please try again.' } };
      }

      if (!verifyData?.success) {
        console.error('Turnstile verification failed:', verifyData);
        return { error: { message: verifyData?.error || 'Bot verification failed. Please try again.' } };
      }
    } catch (err) {
      console.error('Turnstile verification exception:', err);
      return { error: { message: 'Verification service unavailable. Please try again.' } };
    }

    // If Turnstile verification passed, proceed with login
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      return { error };
    }

    return { error: null };
  };

  const signUp = async (email: string, password: string, name: string, turnstileToken: string) => {
    // First, verify the Turnstile token
    try {
      const { data: verifyData, error: verifyError } = await supabase.functions.invoke('verify-turnstile', {
        body: { token: turnstileToken }
      });

      if (verifyError) {
        console.error('Turnstile verification error:', verifyError);
        return { error: { message: 'Verification failed. Please try again.' } };
      }

      if (!verifyData?.success) {
        console.error('Turnstile verification failed:', verifyData);
        return { error: { message: verifyData?.error || 'Bot verification failed. Please try again.' } };
      }
    } catch (err) {
      console.error('Turnstile verification exception:', err);
      return { error: { message: 'Verification service unavailable. Please try again.' } };
    }

    // If Turnstile verification passed, proceed with signup
    const redirectUrl = `${window.location.origin}/dashboard`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: name
        }
      }
    });

    if (error) {
      return { error };
    }

    return { error: null };
  };

  const resetPassword = async (email: string, turnstileToken: string) => {
    // First, verify the Turnstile token
    try {
      const { data: verifyData, error: verifyError } = await supabase.functions.invoke('verify-turnstile', {
        body: { token: turnstileToken }
      });

      if (verifyError) {
        console.error('Turnstile verification error:', verifyError);
        return { error: { message: 'Verification failed. Please try again.' } };
      }

      if (!verifyData?.success) {
        console.error('Turnstile verification failed:', verifyData);
        return { error: { message: verifyData?.error || 'Bot verification failed. Please try again.' } };
      }
    } catch (err) {
      console.error('Turnstile verification exception:', err);
      return { error: { message: 'Verification service unavailable. Please try again.' } };
    }

    // If Turnstile verification passed, proceed with password reset
    try {
      const { data, error } = await supabase.functions.invoke('send-reset-password-email', {
        body: { email }
      });

      if (error) {
        console.error('Reset password error:', error);
        return { error: { message: 'Error sending recovery email. Please try again.' } };
      }

      if (data?.error) {
        return { error: { message: data.error } };
      }

      return { error: null };
    } catch (err) {
      console.error('Reset password exception:', err);
      return { error: { message: 'Error sending recovery email. Please try again.' } };
    }
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      throw error;
    }
    navigate('/');
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      signInWithGoogle, 
      signInWithEmail,
      signUp,
      resetPassword,
      signOut, 
      loading 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    // Return safe defaults instead of throwing during initial render
    return {
      user: null,
      session: null,
      signInWithGoogle: async () => {},
      signInWithEmail: async (_email: string, _password: string, _turnstileToken: string) => ({ error: null }),
      signUp: async (_email: string, _password: string, _name: string, _turnstileToken: string) => ({ error: null }),
      resetPassword: async (_email: string, _turnstileToken: string) => ({ error: null }),
      signOut: async () => {},
      loading: true
    };
  }
  return context;
}
